import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Grid,
    CircularProgress,
    TableContainer
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import TablePager from '../../components/atom/TablePager';
import ActionIconButton from '../../components/atom/ActionIconButton';
import { doSwaggerCall, useSwaggerClient } from '../../hooks/useSwaggerClient';
import SortableTableCell from '../../components/atom/SortableTableCell';
import { useSorting } from '../../hooks/useSorting';
import { BookingInformationIcon, DeleteIcon, UserIcon } from '../../icons';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import { useNotify } from '../../hooks/useNotify';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import FilterLine from '../../components/filter/FilterLine';

const CUSTOM_BOOKINGS_FILTER = [
    {
        key: 'activityStart',
        rangeKeys: ['fromDate', 'toDate'],
        label: 'Activity start time',
        type: 'daterange'
    },
    {
        key: 'activityName',
        label: 'Activity name',
        type: 'text'
    },
    {
        key: 'voucherNumber',
        label: 'Voucher number',
        type: 'text'
    },
    {
        key: 'confirmationCode',
        label: 'Confirmation code',
        type: 'text'
    },
    {
        key: 'agent',
        label: 'Agent name',
        type: 'agentautocomplete'
    },
    {
        key: 'guest',
        label: 'Guest name',
        type: 'guestautocomplete'
    },
    {
        key: 'bookingId',
        label: 'Reservation ID',
        type: 'text'
    },
    {
        key: 'activityId',
        label: 'Activity ID',
        type: 'text'
    },
    {
        key: 'vendorCode',
        label: 'Vendor code',
        type: 'text'
    }
];

function getFilterFromUrl(urlSearchPart) {
    const searchParams = new URLSearchParams(urlSearchPart.trim() !== '' ? urlSearchPart : '');
    const fromDate =
        searchParams.get('datefrom') !== null && moment(searchParams.get('datefrom')).isValid()
            ? moment(searchParams.get('datefrom'))
            : null;
    const toDate =
        searchParams.get('dateto') !== null && moment(searchParams.get('dateto')).isValid()
            ? moment(searchParams.get('dateto'))
            : null;

    const bookingIdParam = searchParams.get('bookingId');
    let bookingId = '';
    if (bookingIdParam) {
        if (bookingIdParam === 'unconverted') {
            bookingId = 'unconverted';
        } else if (!Number.isNaN(parseInt(bookingIdParam, 10))) {
            bookingId = parseInt(bookingIdParam, 10);
        }
    }

    return {
        fromDate,
        toDate,
        activityName: searchParams.get('activityName') || '',
        voucherNumber: searchParams.get('voucherNumber') || '',
        confirmationCode: searchParams.get('confirmationCode') || '',
        agent: searchParams.get('agent') || null,
        guest: searchParams.get('guest') || null,
        bookingId, // not parsed, since can be 'unconverted' or number
        activityId: !Number.isNaN(parseInt(searchParams.get('activityId'), 10))
            ? parseInt(searchParams.get('activityId'), 10)
            : '',
        vendorCode: searchParams.get('vendorCode') || ''
    };
}

function getUrlFromFilter(filter) {
    const search = new URLSearchParams();

    if (filter.fromDate) {
        search.append('datefrom', filter.fromDate.format('YYYY-MM-DD'));
    }
    if (filter.toDate) {
        search.append('dateto', filter.toDate.format('YYYY-MM-DD'));
    }
    if (filter.activityName) {
        search.append('activityName', filter.activityName);
    }
    if (filter.voucherNumber) {
        search.append('voucherNumber', filter.voucherNumber);
    }
    if (filter.confirmationCode) {
        search.append('confirmationCode', filter.confirmationCode);
    }
    if (filter.agent) {
        search.append('agent', filter.agent);
    }
    if (filter.guest) {
        search.append('guest', filter.guest);
    }
    if (filter.bookingId) {
        search.append('bookingId', filter.bookingId.toString());
    }
    if (filter.activityId) {
        search.append('activityId', filter.activityId.toString());
    }
    if (filter.vendorCode) {
        search.append('vendorCode', filter.vendorCode);
    }

    const searchString = search.toString();
    return `?${searchString}`;
}

function CustomBookingsPage() {
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const { sort, sortDir, setSorting } = useSorting(['activity_start', 'DESC']);
    const { showModal } = useModals();
    const location = useLocation();
    const history = useHistory();
    const [currentFilters, setFilters] = useState(getFilterFromUrl(location.search));
    const { notifyError, notifySuccess } = useNotify();

    const setCurrentFilters = useCallback(
        newfilters => history.replace(getUrlFromFilter(newfilters)),
        [history]
    );

    useEffect(() => {
        setFilters(getFilterFromUrl(location.search));
    }, [location.search, setFilters]);

    const [data, loading, error, setData, reload] = useSwaggerClient(
        'bookings',
        'getCustomBookings',
        {
            page,
            pageSize,
            sort,
            sortDir,
            fromDate: moment(currentFilters.fromDate).isValid()
                ? moment(currentFilters.fromDate).format('YYYY-MM-DD')
                : '',
            toDate: moment(currentFilters.toDate).isValid()
                ? moment(currentFilters.toDate).format('YYYY-MM-DD')
                : '',
            activityName: currentFilters.activityName,
            voucherNumber: currentFilters.voucherNumber,
            confirmationCode: currentFilters.confirmationCode,
            agentList: currentFilters.agent,
            guestList: currentFilters.guest,
            bookingId: currentFilters.bookingId,
            activityId: currentFilters.activityId,
            vendorCode: currentFilters.vendorCode
        }
    );

    const resetFilters = () =>
        setCurrentFilters({
            fromDate: null,
            toDate: null,
            activityName: '',
            voucherNumber: '',
            confirmationCode: '',
            agent: null,
            guest: null,
            bookingId: '',
            activityId: '',
            vendorCode: ''
        });

    useEffect(() => {
        if (loading) {
            return;
        }

        if (data?.bookings.length === 0 && page !== 0) {
            setPage(0);
        }
    }, [page, data, loading, setPage]);

    const deleteCustomBooking = useCallback(
        bookingId => {
            const req = doSwaggerCall('bookings', 'deleteCustomBooking', {
                customBookingId: bookingId
            });
            req.then(() => {
                notifySuccess('Custom booking deleted!');
                reload();
            }).catch(err => {
                notifyError(
                    err.response?.body?.message || err.message || 'Cannot delete custom booking!'
                );
            });
        },
        [setData, data, notifyError, notifySuccess]
    );

    return (
        <Grid container className="golf-reservation">
            <Grid item xs={12}>
                <FilterLine
                    possibleFilters={CUSTOM_BOOKINGS_FILTER}
                    filters={currentFilters}
                    setFilters={setCurrentFilters}
                    resetFilters={resetFilters}
                />
            </Grid>
            <Grid item xs={12}>
                {loading && data?.bookings.length === 0 ? (
                    <CircularProgress />
                ) : (
                    <>
                        <TableContainer style={{ overflowX: 'auto' }}>
                            <Table
                                className="mt-2"
                                style={{ backgroundColor: 'white' }}
                                size="small"
                            >
                                <TableHead>
                                    <TableRow>
                                        <SortableTableCell
                                            arrow={sort === 'id' ? sortDir : ''}
                                            onClick={() => setSorting('id')}
                                        >
                                            ID
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'booking_id' ? sortDir : ''}
                                            onClick={() => setSorting('booking_id')}
                                        >
                                            Reservation ID
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'activity_id' ? sortDir : ''}
                                            onClick={() => setSorting('activity_id')}
                                        >
                                            Activity ID
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'activity_name' ? sortDir : ''}
                                            onClick={() => setSorting('activity_name')}
                                        >
                                            Activity name
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'activity_start' ? sortDir : ''}
                                            onClick={() => setSorting('activity_start')}
                                        >
                                            Activity start time
                                        </SortableTableCell>
                                        <TableCell>Guest</TableCell>
                                        <TableCell>Agent </TableCell>
                                        <SortableTableCell
                                            arrow={sort === 'vendor_code' ? sortDir : ''}
                                            onClick={() => setSorting('vendor_code')}
                                        >
                                            Vendor code
                                        </SortableTableCell>
                                        <SortableTableCell
                                            arrow={sort === 'voucher_number' ? sortDir : ''}
                                            onClick={() => setSorting('voucher_number')}
                                        >
                                            Voucher number
                                        </SortableTableCell>
                                        <TableCell>Confirmation code</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {error && (
                                        <TableRow>
                                            <TableCell colSpan={11} style={{ textAlign: 'center' }}>
                                                <Alert severity="error">
                                                    <h4>
                                                        {error.response.body.message ||
                                                            error.message}
                                                    </h4>
                                                </Alert>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {data &&
                                        data.bookings &&
                                        data.bookings.map(booking => {
                                            return (
                                                <TableRow key={booking.id}>
                                                    <TableCell>{booking.id}</TableCell>
                                                    <TableCell>
                                                        {booking?.booking_id || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {booking?.activity_id || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {booking?.activity_name || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {moment
                                                            .utc(booking?.activity_start)
                                                            .format('MM/DD/YYYY, h:mm a')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {booking?.guest_name ||
                                                            booking?.guest_id ||
                                                            '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {booking?.agent_name || booking.agent_id}
                                                    </TableCell>
                                                    <TableCell>
                                                        {booking?.vendor_code || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {booking?.voucher_number || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {booking?.confirmation_code || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <ActionIconButton
                                                            title="Booking informations"
                                                            icon={BookingInformationIcon}
                                                            disabled={!booking.booking_id}
                                                            to={`/client/bookings/${booking.booking_id}`}
                                                            target="_blank"
                                                        />
                                                        {/* TODO: can jump to any? */}
                                                        <ActionIconButton
                                                            title="Guest informations"
                                                            icon={UserIcon}
                                                            disabled={!booking.guest_id}
                                                            to={`/guests/${booking.guest_id}`}
                                                            target="_blank"
                                                        />
                                                        <ActionIconButton
                                                            title="Delete"
                                                            icon={DeleteIcon}
                                                            onClick={() => {
                                                                showModal(MODAL_TYPE.CONFIRMATION, {
                                                                    title: `Are you sure you want to remove ${
                                                                        booking?.activity_name
                                                                            ? booking?.activity_name
                                                                            : 'activty'
                                                                    }?`,
                                                                    content: 'This action is irreversible.',
                                                                    confirm: () =>
                                                                        deleteCustomBooking(booking.id)
                                                                });
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Grid container justifyContent="flex-end">
                            <TablePager
                                total={data?.total}
                                page={page}
                                setPage={setPage}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Grid>
    );
}

export default CustomBookingsPage;
